// react
import { ChangeEvent, FormEvent, useContext, useState } from 'react';

// types
import { ActionStatus, ErrorCause, LoginType, User } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useNavigate } from 'react-router-dom';

// jwt
import { jwtDecode } from 'jwt-decode';

// context
import { ContextUser } from '../../../App.context';

// mui
import { Alert, Button, Grid, InputLabel, SelectChangeEvent, TextField } from '@mui/material';

// fields
import { InputPhone } from '../../fields/InputPhone/InputPhone';

// utils
import { sanitizePhone } from '../../../utils/strings';

// insights
import { appInsights, trackEvent } from '../../../insights';

// props
export interface CountryPayload {
	code: string;
	smsPrefix: number;
}

export interface CreateJWTPayload {
	country?: CountryPayload;
	email?: string;
	otp: string;
	sms?: number;
}

export interface CreateOTPPayload {
	country?: CountryPayload;
	email?: string;
	sms?: number;
}

export interface FormLoginProps {
	loginType: LoginType;
}

export const FormLogin = ({ loginType }: FormLoginProps) => {
	// hooks
	const navigate = useNavigate();
	const { t } = useTranslation();

	// state
	const [error, setError] = useState<string | null>(null);
	const [hasOtp, setHasOtp] = useState(false);
	const [statusLogin, setStatusLogin] = useState<ActionStatus>('DEFAULT');
	const [statusOtp, setStatusOtp] = useState<ActionStatus>('DEFAULT');
	const [values, setValues] = useState({
		country: {
			code: 'US',
			smsPrefix: 1,
		},
		email: '',
		otp: '',
		sms: '',
	});

	// context
	const { setUser } = useContext(ContextUser);

	// vars
	const idFormLogin = `form-login`;
	const idFormOtp = `form-otp`;

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name]: value,
		});
	};

	const trackFetch = async (idOperation: string, url: string, response: Response) => {
		appInsights.trackDependencyData({
			id: idOperation,
			name: url,
			responseCode: response.status,
			success: response.ok,
			correlationContext: idOperation,
		});
		trackEvent('DataFetched', { success: true });
	};

	const createHeaders = async (idOperation: string) => {
		return {
			'Content-Type': 'application/json',
			apikey: process.env.REACT_APP_APIKEY || '',
			'Request-Id': idOperation,
			'X-Operation-Id': idOperation,
		};
	};

	const handleSubmitLogin = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		// clear error
		setError(null);
		setStatusLogin('LOADING');

		let body: CreateJWTPayload = {
			otp: values.otp,
		};

		if (values.country) {
			body.country = values.country;
		}

		if (values.email) {
			body.email = values.email;
		}

		if (values.sms) {
			body.sms = sanitizePhone(values.sms) as number;
		}

		try {
			const idOperation = crypto.randomUUID();
			const url = `${process.env['REACT_APP_API_URL']}/api/CreateJWT`;
			const response = await fetch(url, {
				method: 'POST',
				headers: await createHeaders(idOperation),
				body: JSON.stringify(body),
			});

			await trackFetch(idOperation, url, response);
			const { status, message } = await response.json();

			if (!status) {
				throw new Error('The OTP is invalid.', {
					cause: {
						id: 'LOGIN_INVALID_OTP',
					},
				});
			}

			const token = message.replace('Bearer ', '');
			const tokenDecoded = jwtDecode(message);

			// store token in session
			sessionStorage.setItem('token', token);
			sessionStorage.setItem('tokenDecoded', JSON.stringify(tokenDecoded));

			// fetch user
			const fetchUserUrl = `${process.env['REACT_APP_API_URL']}/api/Proxies/users/${tokenDecoded.sub}?populate=entities`;
			const fetchUserResponse = await fetch(fetchUserUrl, {
				headers: {
					Authorization: `Bearer ${token}`,
					apikey: process.env.REACT_APP_APIKEY || '',
				},
			});

			if (!fetchUserResponse.ok) {
				throw new Error('Failed to fetch user data.', {
					cause: {
						id: 'LOGIN_USER_NOT_FOUND',
					},
				});
			}

			const resFetchUser = await fetchUserResponse.json();

			if (!resFetchUser.data?.length) {
				throw new Error('The user could not be found.', {
					cause: {
						id: 'LOGIN_USER_NOT_FOUND',
					},
				});
			}

			const user = resFetchUser.data[0] as User;

			// store user in session
			sessionStorage.setItem('user', JSON.stringify(user));

			// set user
			setUser(user);

			// navigate to dashboard
			navigate('/dashboard');
			setStatusLogin('SUCCESS');
		} catch (error) {
			setStatusLogin('ERROR');
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			let message = t('error.default');

			if (cause.id === 'LOGIN_INVALID_OTP') {
				message = t('error.otpInvalid');
			}

			// set error
			setError(message);
			trackEvent('DataFetchError', { message: error });
		}
	};

	const handleSubmitOtp = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		// clear error
		setError(null);
		setStatusOtp('LOADING');

		let body: CreateOTPPayload = {};

		if (values.country) {
			body.country = values.country;
		}

		if (values.email) {
			body.email = values.email;
		}

		if (values.sms) {
			body.sms = sanitizePhone(values.sms) as number;
		}

		try {
			const idOperation = crypto.randomUUID();
			const url = `${process.env['REACT_APP_API_URL']}/api/CreateOTP`;
			const response = await fetch(url, {
				method: 'POST',
				headers: await createHeaders(idOperation),
				body: JSON.stringify(body),
			});
			await trackFetch(idOperation, url, response);
			const { status } = await response.json();

			if (!status) {
				throw new Error('The user could not be found.', {
					cause: {
						id: 'LOGIN_USER_NOT_FOUND',
					},
				});
			}

			setHasOtp(true);
			setStatusOtp('SUCCESS');
		} catch (error) {
			setStatusOtp('ERROR');
			let message = t('error.default');

			// set error
			setError(message);

			// track event
			trackEvent('DataFetchError', { message: error });
		}
	};

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				{hasOtp ? (
					<form id={idFormLogin} onSubmit={handleSubmitLogin}>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<InputLabel htmlFor={`${idFormLogin}-otp`} required={true}>
									{t('otp')}
								</InputLabel>
								<TextField
									fullWidth
									id={`${idFormLogin}-otp`}
									name="otp"
									onChange={handleChangeValue}
									required={true}
									size="small"
									type="number"
									value={values.otp}
								/>
							</Grid>
							<Grid item xs={12}>
								<Button
									disabled={statusLogin === 'LOADING'}
									fullWidth={true}
									type="submit"
									variant="contained">
									{statusLogin === 'LOADING' ? t('loading') : t('signIn')}
								</Button>
							</Grid>
						</Grid>
					</form>
				) : (
					<form id={idFormOtp} onSubmit={handleSubmitOtp}>
						<Grid container spacing={2}>
							{loginType === 'email' && (
								<Grid item xs={12}>
									<InputLabel htmlFor={`${idFormOtp}-email`} required={true}>
										{t('email')}
									</InputLabel>
									<TextField
										fullWidth
										id={`${idFormOtp}-email`}
										name="email"
										placeholder="user@domain.com"
										onChange={handleChangeValue}
										required={true}
										size="small"
										type="email"
										value={values.email}
									/>
								</Grid>
							)}
							{loginType === 'sms' && (
								<Grid item xs={12}>
									<InputLabel htmlFor={`${idFormOtp}-sms`} required={true}>
										{t('phoneNumber')}
									</InputLabel>
									<InputPhone
										defaultCountry="US"
										id={`${idFormOtp}-sms`}
										name="sms"
										onChange={(e, values) => {
											setValues((valuesPrev) => {
												return {
													...valuesPrev,
													country: values.country,
													sms: values.sms,
												};
											});
										}}
										required={true}
										size="small"
									/>
								</Grid>
							)}
							<Grid item xs={12}>
								<Button
									color="primary"
									disabled={statusOtp === 'LOADING'}
									fullWidth
									type="submit"
									variant="contained">
									{statusOtp === 'LOADING' ? t('loading') : t('continue')}
								</Button>
							</Grid>
						</Grid>
					</form>
				)}
			</Grid>
			{error && (
				<Grid item xs={12}>
					<Alert severity="error">{error}</Alert>
				</Grid>
			)}
		</Grid>
	);
};
